import { type Editor } from '@tiptap/core';
import { type Node as ProsemirrorNode } from '@tiptap/pm/model';
import { NodeViewContent, NodeViewWrapper } from '@tiptap/react';
import { type BundledLanguage } from 'shiki';

import { Icons } from '@/assets/icons/icons';
import CopyButton from '@/components/blocks/CopyButton';
import { IconButton } from '@/components/ui/IconButton';
import { XStack } from '@/components/ui/layout';
import { SearchableDropdown } from '@/components/ui/SearchableDropdown';
import { classList } from '@/utils/cn';

interface CodeBlockComponentProps {
  node: ProsemirrorNode;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  updateAttributes: (attributes: Record<string, any>) => void;
  editor: Editor;
  getPos: () => number | undefined;
  deleteNode: () => void;
}

// Comprehensive list of Shiki supported languages
const LANGUAGES = [
  // Most common languages first
  { value: 'javascript', label: 'JavaScript' },
  { value: 'typescript', label: 'TypeScript' },
  { value: 'python', label: 'Python' },
  { value: 'java', label: 'Java' },
  { value: 'c', label: 'C' },
  { value: 'cpp', label: 'C++' },
  { value: 'csharp', label: 'C#' },
  { value: 'go', label: 'Go' },
  { value: 'rust', label: 'Rust' },
  { value: 'php', label: 'PHP' },
  { value: 'ruby', label: 'Ruby' },
  { value: 'swift', label: 'Swift' },
  { value: 'kotlin', label: 'Kotlin' },
  { value: 'html', label: 'HTML' },
  { value: 'css', label: 'CSS' },
  { value: 'scss', label: 'SCSS' },
  { value: 'json', label: 'JSON' },
  { value: 'xml', label: 'XML' },
  { value: 'yaml', label: 'YAML' },
  { value: 'markdown', label: 'Markdown' },
  { value: 'shellscript', label: 'Shell' },
  { value: 'sql', label: 'SQL' },

  // Web frameworks & libraries
  { value: 'jsx', label: 'JSX' },
  { value: 'tsx', label: 'TSX' },
  { value: 'vue', label: 'Vue' },
  { value: 'vue-html', label: 'Vue Template' },
  { value: 'svelte', label: 'Svelte' },
  { value: 'angular-html', label: 'Angular Template' },
  { value: 'angular-ts', label: 'Angular TypeScript' },

  // CSS preprocessors & tools
  { value: 'sass', label: 'Sass' },
  { value: 'less', label: 'Less' },
  { value: 'stylus', label: 'Stylus' },
  { value: 'postcss', label: 'PostCSS' },

  // Config & data formats
  { value: 'jsonc', label: 'JSON with Comments' },
  { value: 'toml', label: 'TOML' },
  { value: 'ini', label: 'INI' },
  { value: 'properties', label: 'Properties' },
  { value: 'dotenv', label: 'Environment' },

  // System & DevOps
  { value: 'makefile', label: 'Makefile' },
  { value: 'cmake', label: 'CMake' },
  { value: 'nginx', label: 'Nginx' },
  { value: 'apache', label: 'Apache Config' },
  { value: 'systemd', label: 'Systemd' },

  // Functional & other paradigms
  { value: 'haskell', label: 'Haskell' },
  { value: 'ocaml', label: 'OCaml' },
  { value: 'fsharp', label: 'F#' },
  { value: 'scala', label: 'Scala' },
  { value: 'elm', label: 'Elm' },
  { value: 'purescript', label: 'PureScript' },
  { value: 'scheme', label: 'Scheme' },
  { value: 'lisp', label: 'Lisp' },
  { value: 'racket', label: 'Racket' },
  { value: 'erlang', label: 'Erlang' },
  { value: 'elixir', label: 'Elixir' },

  // JVM languages
  { value: 'groovy', label: 'Groovy' },
  { value: 'clojure', label: 'Clojure' },

  // Mobile development
  { value: 'objective-c', label: 'Objective-C' },
  { value: 'dart', label: 'Dart' },

  // Scientific & statistical
  { value: 'r', label: 'R' },
  { value: 'julia', label: 'Julia' },
  { value: 'matlab', label: 'MATLAB' },
  { value: 'stata', label: 'Stata' },
  { value: 'sas', label: 'SAS' },

  // Scripting languages
  { value: 'perl', label: 'Perl' },
  { value: 'raku', label: 'Raku (Perl 6)' },
  { value: 'lua', label: 'Lua' },
  { value: 'tcl', label: 'Tcl' },
  { value: 'powershell', label: 'PowerShell' },
  { value: 'batch', label: 'Batch' },

  // Markup & templating
  { value: 'latex', label: 'LaTeX' },
  { value: 'tex', label: 'TeX' },
  { value: 'bibtex', label: 'BibTeX' },
  { value: 'rst', label: 'reStructuredText' },
  { value: 'asciidoc', label: 'AsciiDoc' },
  { value: 'wiki', label: 'Wiki' },
  { value: 'pug', label: 'Pug' },
  { value: 'jade', label: 'Jade' },
  { value: 'haml', label: 'Haml' },
  { value: 'handlebars', label: 'Handlebars' },
  { value: 'jinja', label: 'Jinja' },
  { value: 'erb', label: 'ERB' },
  { value: 'twig', label: 'Twig' },
  { value: 'liquid', label: 'Liquid' },
  { value: 'razor', label: 'Razor' },

  // Domain-specific languages
  { value: 'graphql', label: 'GraphQL' },
  { value: 'protobuf', label: 'Protocol Buffers' },

  // Query languages
  { value: 'cypher', label: 'Cypher' },
  { value: 'sparql', label: 'SPARQL' },

  // Assembly & low-level
  { value: 'asm', label: 'Assembly' },

  // Legacy & specialized
  { value: 'cobol', label: 'COBOL' },
  { value: 'fortran-fixed-form', label: 'Fortran' },
  { value: 'pascal', label: 'Pascal' },
  { value: 'vb', label: 'Visual Basic' },
  { value: 'ada', label: 'Ada' },
  { value: 'smalltalk', label: 'Smalltalk' },
  { value: 'prolog', label: 'Prolog' },
  { value: 'logo', label: 'Logo' },

  // Niche & specialized
  { value: 'zig', label: 'Zig' },
  { value: 'nim', label: 'Nim' },
  { value: 'crystal', label: 'Crystal' },
  { value: 'd', label: 'D' },
  { value: 'vala', label: 'Vala' },

  // Virtualization & containers
  { value: 'docker', label: 'Docker' },
  { value: 'dockerfile', label: 'Dockerfile' },

  // Configuration languages
  { value: 'hcl', label: 'HCL' },
  { value: 'terraform', label: 'Terraform' },
  { value: 'puppet', label: 'Puppet' },

  // Editors & IDEs
  { value: 'vim', label: 'Vim' },
  { value: 'viml', label: 'VimL' },

  // Documentation
  { value: 'mdx', label: 'MDX' },

  // Shader languages
  { value: 'glsl', label: 'GLSL' },
  { value: 'hlsl', label: 'HLSL' },
  { value: 'shaderlab', label: 'ShaderLab' },
  { value: 'wgsl', label: 'WGSL' },

  // Web Assembly
  { value: 'wasm', label: 'WebAssembly' },

  // Game development
  { value: 'gdscript', label: 'GDScript' },

  // International languages
  { value: 'wenyan', label: '文言 (Classical Chinese)' },

  // Special formats
  { value: 'log', label: 'Log Files' },
  { value: 'diff', label: 'Diff' },
  { value: 'csv', label: 'CSV' },
  { value: 'tsv', label: 'TSV' },

  // Plain text variants
  { value: 'plaintext', label: 'Plain Text' },
] as const satisfies {
  value: BundledLanguage | 'plaintext';
  label: string;
}[];

export function CodeBlockView({
  node,
  updateAttributes,
  deleteNode,
}: CodeBlockComponentProps) {
  const language = node.attrs.language || 'plaintext';
  const code = node.textContent;

  const handleLanguageChange = (newLanguage: string) => {
    updateAttributes({ language: newLanguage });
  };

  const handleDelete = () => {
    deleteNode();
  };

  return (
    <NodeViewWrapper
      className={classList(
        'relative group',
        'rounded-lg smoothing-default',
        'bg-surface-active',
        'not-prose',
        'font-mono',
        'my-4'
      )}
    >
      {/* Header with language selector and actions */}
      <div className={classList('flex items-center justify-end', 'pt-1 pr-1')}>
        <XStack
          className={classList(
            'gap-1 items-center h-8',
            'border border-border-strongest rounded-md px-1',
            'opacity-0 touch:opacity-100 group-hover:opacity-100 has-[:focus-visible]:opacity-100 group-has-[:focus-visible]:opacity-100 transition-opacity'
          )}
        >
          <SearchableDropdown
            options={LANGUAGES}
            value={language}
            placeholder="Plain Text"
            searchPlaceholder="Search for a language"
            onValueChange={handleLanguageChange}
            triggerClassName={classList(
              'w-auto h-5 text-xs bg-transparent gap-1 font-sans text-foreground-subtle pl-1.5 pr-1'
            )}
            contentClassName="max-w-60"
            emptyResult="No language found"
          />
          <div className="w-px h-4 bg-border-strongest" />
          <CopyButton
            text={code}
            copyAsPlainText
            size="xs"
            variant="ghost"
            copyText="Copy"
            tooltipOptions={{ sideOffset: 9 }}
          />
          <IconButton
            icon={Icons.trash}
            size="xs"
            variant="ghost"
            tooltip="Delete"
            onClick={handleDelete}
            tooltipOptions={{ sideOffset: 9 }}
          />
        </XStack>
      </div>

      {/* Code content */}
      <div className="relative">
        <NodeViewContent
          className={classList(
            'px-7 pt-0 pb-9 m-0',
            'font-mono',
            'overflow-x-auto',
            'bg-transparent',
            'outline-none',
            'resize-none',
            'whitespace-pre'
          )}
        />
      </div>
    </NodeViewWrapper>
  );
}
